import React, { useState } from 'react'
import { getMediumAssetPath } from '@aytm/lib/common/assets.ts'
import { formatPriceWithLocale } from '@aytm/app/swidget/base/questions/Ecommerce/functions.ts'
import { EcommerceResponse, IEcommerceQuestionSchema } from '@aytm/app/swidget/base/questions/Ecommerce/types'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink.tsx'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useTimeline from '@aytm/app/swidget/base/modules/timeline/useTimeline.ts'
import style from './Cart.module.scss'
import AddToCartButton from './AddToCartButton'

export interface ICartProps {
  response: EcommerceResponse
  schema: IEcommerceQuestionSchema
  totals?: number[]
  deliveryDates: string[]
  findability: boolean
  onAddToCart: (qnt: number, index: number, dropdown?: boolean) => void
  onCartClose?: () => void
  onItemSelect: (index: number) => void
  checkout?: () => void
  onUpdateResponse: (response: EcommerceResponse) => void
}

const Cart = ({
  response,
  schema,
  onAddToCart,
  deliveryDates,
  findability,
  onCartClose,
  onItemSelect,
  totals,
  checkout,
  onUpdateResponse
}: ICartProps) => {
  const timeline = useTimeline()
  const viewport = useViewport()
  const [openSummary, setOpenSummary] = useState<boolean>(true)
  const { t, languageTag } = useSurveyLocale()
  const na = schema.answers.find(answer => answer.na)
  const valid =
    (schema.max_money_spent ? totals[1] <= schema.max_money_spent : true) &&
    (schema.max_cart_size ? totals[0] <= schema.max_cart_size : true)

  const onDeleteFromCart = (index: number) => {
    response.answers[index].v = 0
    timeline.log({
      type: 'delete_from_cart',
      ref: response.answers[index].id
    })
    onUpdateResponse(response)
  }

  const onNaSelect = () => {
    timeline.log({
      type: 'na_select',
      ref: na.id
    })
    const answers = response.answers.map(answer => {
      answer.v = answer.id === na.id ? 1 : 0
      return answer
    })
    response.answers = answers
    onUpdateResponse(response)
  }

  const renderError = () => {
    if (!valid) {
      return (
        <div
          className={style.error}
          dangerouslySetInnerHTML={{
            __html:
              (schema.max_money_spent && totals[1] > schema.max_money_spent
                ? t<(max: string) => string>(
                    'questions',
                    'ec',
                    'maxSpentError'
                  )(formatPriceWithLocale(schema.max_money_spent, schema.price_mask, languageTag))
                : '') +
              (schema.max_cart_size && totals[0] > schema.max_cart_size
                ? t<(max: number) => string>('questions', 'ec', 'maxCartCountError')(schema.max_cart_size)
                : '')
          }}
        />
      )
    }
    return null
  }

  return (
    <>
      {totals[0] > 0 && !viewport.mobile && (
        <div className={style.wrapper}>
          <button className={style.back} onClick={onCartClose}>
            {t('questions', 'ec', 'target', 'back')}
          </button>
        </div>
      )}
      {totals[0] > 0 ? (
        <div className={`${style.cart} ${viewport.mobile ? style.cartMobile : ''}`}>
          {viewport.mobile && (
            <div className={style.cartHead}>
              <h6>{t('questions', 'ec', 'target', 'cart')}</h6>
              <div
                className={style.total}
                dangerouslySetInnerHTML={{
                  __html: t<(price: string, items: number) => string>(
                    'questions',
                    'ec',
                    'target',
                    'cartHead'
                  )(formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false), totals[0])
                }}
              />
            </div>
          )}
          <div className={style.content}>
            {!viewport.mobile && (
              <>
                <h6>{t('questions', 'ec', 'target', 'cart')}</h6>
                <div
                  className={style.total}
                  dangerouslySetInnerHTML={{
                    __html: t<(price: string, items: number) => string>(
                      'questions',
                      'ec',
                      'target',
                      'cartHead'
                    )(formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false), totals[0])
                  }}
                />
              </>
            )}
            <div className={style.products}>
              <div className={style.shipping}>
                <div className={style.title}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" id="Ship">
                    <path fill="#BA9874" d="M2.67 26.67h8V5.33h-8z"></path>
                    <path
                      fill="#CEAF8C"
                      d="M8.67 5.33v4l-1-.66-1 .66-1-.66-1 .66v-4zm0 21.34v-4l-1 .66-1-.66-1 .66-1-.66v4zM29.33 5.33v21.34H10.67V5.33z"
                    ></path>
                    <path fill="#C00" d="M20 17.55a1.56 1.56 0 1 0 0-3.1 1.56 1.56 0 0 0 0 3.1"></path>
                    <path
                      fill="#C00"
                      d="M24.67 16a4.67 4.67 0 1 1-9.34 0 4.67 4.67 0 0 1 9.34 0m-7.78 0a3.11 3.11 0 1 0 6.22 0 3.11 3.11 0 0 0-6.22 0"
                    ></path>
                  </svg>
                  {t('questions', 'ec', 'target', 'shipping')}
                </div>
                <EmptyLink color="#CC0000">
                  <span className={style.edit}>{t('questions', 'ec', 'target', 'editItems')}</span>
                </EmptyLink>
              </div>
              {response.answers.map((item, index) => (
                <div key={index}>
                  {item.v > 0 && (
                    <div className={style.product}>
                      {!viewport.mobile && (
                        <button className={style.delete} onClick={() => onDeleteFromCart(index)}>
                          <i className="pi pi-times" />
                        </button>
                      )}
                      {viewport.mobile && (
                        <div
                          className={style.delivery}
                          dangerouslySetInnerHTML={{
                            __html: t<(value: number, date: string) => string>(
                              'questions',
                              'ec',
                              'target',
                              'itemsArrives'
                            )(item.v, deliveryDates[index])
                          }}
                        />
                      )}
                      {schema.answers[index].image && schema.answers[index].image.original_url ? (
                        <img
                          className={style.image}
                          alt={schema.answers[index].image?.alt_text || t('questions', 'img', 'noAlt')}
                          src={getMediumAssetPath(schema.answers[index].image)}
                          onClick={() => onItemSelect(index)}
                        />
                      ) : (
                        <div className={style.noImage} onClick={() => onItemSelect(index)} />
                      )}
                      <div className={style.productContent}>
                        <div className={style.info}>
                          {viewport.mobile && (
                            <div className={style.price}>
                              {schema.answers[index].discounted_price &&
                              schema.answers[index].price > schema.answers[index].discounted_price ? (
                                <>
                                  <div
                                    className={style.discountedPrice}
                                    dangerouslySetInnerHTML={{
                                      __html: formatPriceWithLocale(
                                        item.v * schema.answers[index].discounted_price,
                                        schema.price_mask,
                                        languageTag,
                                        true,
                                        false
                                      )
                                    }}
                                  />
                                  <div className={style.lastPrice}>
                                    <span className={style.reg}>{t('questions', 'ec', 'target', 'was')}</span>
                                    <span
                                      className={style.priceLt}
                                      dangerouslySetInnerHTML={{
                                        __html: formatPriceWithLocale(
                                          item.v * schema.answers[index].price,
                                          schema.price_mask,
                                          languageTag,
                                          true,
                                          false
                                        )
                                      }}
                                    />
                                  </div>
                                  {item.v > 1 && (
                                    <div
                                      className={style.each}
                                      dangerouslySetInnerHTML={{
                                        __html: `each ${formatPriceWithLocale(item.price, schema.price_mask, languageTag, true, false)}`
                                      }}
                                    />
                                  )}

                                  <div className={style.sale}>{t('questions', 'ec', 'target', 'newLowerPrice')}</div>
                                </>
                              ) : (
                                <>
                                  <div
                                    className={style.defaultPrice}
                                    dangerouslySetInnerHTML={{
                                      __html: formatPriceWithLocale(
                                        item.v * response.answers[index].price,
                                        schema.price_mask,
                                        languageTag,
                                        true,
                                        false
                                      )
                                    }}
                                  />
                                  {item.v > 1 && (
                                    <div
                                      className={style.each}
                                      dangerouslySetInnerHTML={{
                                        __html: `${t('questions', 'ec', 'target', 'each')} ${formatPriceWithLocale(
                                          item.price,
                                          schema.price_mask,
                                          languageTag,
                                          true,
                                          false
                                        )}`
                                      }}
                                    />
                                  )}
                                </>
                              )}
                            </div>
                          )}
                          <div
                            className={style.caption}
                            onClick={() => onItemSelect(index)}
                            dangerouslySetInnerHTML={{ __html: schema.answers[index].text }}
                          />
                          <div className={style.sold}>{t('questions', 'ec', 'target', 'soldByTarget')}</div>
                          <div className={style.buttons}>
                            <AddToCartButton
                              appearence="spin"
                              index={index}
                              value={item.v}
                              findability={findability}
                              limit={schema.max_qty_per_product}
                              size="small"
                              width="110px"
                              onAddToCart={onAddToCart}
                            />
                            {!viewport.mobile && (
                              <EmptyLink color="#CC0000">
                                <div className={style.save}>{t('questions', 'ec', 'target', 'saveForLater')}</div>
                              </EmptyLink>
                            )}
                          </div>
                        </div>
                        {!viewport.mobile && (
                          <div className={style.shippingInfo}>
                            <div className={style.shippingWrapper}>
                              <div className={style.avaliable}></div>
                              <div className={style.shippingOptions}>
                                <div className={style.titleAvaliable}>{t('questions', 'ec', 'target', 'shipping')}</div>
                                <div className={style.dateAvaliable}>
                                  {t<(date: string) => string>(
                                    'questions',
                                    'ec',
                                    'target',
                                    'arrivingBy'
                                  )(deliveryDates[index])}
                                </div>
                              </div>
                            </div>
                            <div className={style.shippingWrapper}>
                              <div className={style.notAvaliable}></div>
                              <div className={style.shippingOptions}>
                                <div className={style.titleNotAvaliable}>
                                  {t('questions', 'ec', 'target', 'naPickup')}
                                </div>
                                <div className={style.dateNotAvaliable}>
                                  {t('questions', 'ec', 'target', 'rapids')}&nbsp;
                                  <EmptyLink color="#CC0000">
                                    <div className={style.changeStore}>{t('questions', 'ec', 'target', 'change')}</div>
                                  </EmptyLink>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      {!viewport.mobile && (
                        <div className={style.price}>
                          {schema.answers[index].discounted_price &&
                          schema.answers[index].price > schema.answers[index].discounted_price ? (
                            <>
                              <div
                                className={style.discountedPrice}
                                dangerouslySetInnerHTML={{
                                  __html: formatPriceWithLocale(
                                    item.v * schema.answers[index].discounted_price,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )
                                }}
                              />
                              <div className={style.lastPrice}>
                                <span className={style.reg}>{t('questions', 'ec', 'target', 'was')}</span>
                                <span
                                  className={style.priceLt}
                                  dangerouslySetInnerHTML={{
                                    __html: formatPriceWithLocale(
                                      item.v * schema.answers[index].price,
                                      schema.price_mask,
                                      languageTag,
                                      true,
                                      false
                                    )
                                  }}
                                />
                              </div>
                              {item.v > 1 && (
                                <div
                                  className={style.each}
                                  dangerouslySetInnerHTML={{
                                    __html: `each ${formatPriceWithLocale(item.price, schema.price_mask, languageTag, true, false)}`
                                  }}
                                />
                              )}
                              <div className={style.sale}>{t('questions', 'ec', 'target', 'newLowerPrice')}</div>
                            </>
                          ) : (
                            <>
                              <div
                                className={style.defaultPrice}
                                dangerouslySetInnerHTML={{
                                  __html: formatPriceWithLocale(
                                    item.v * response.answers[index].price,
                                    schema.price_mask,
                                    languageTag,
                                    true,
                                    false
                                  )
                                }}
                              />
                              {item.v > 1 && (
                                <div
                                  className={style.each}
                                  dangerouslySetInnerHTML={{
                                    __html: `${t('questions', 'ec', 'target', 'each')} ${formatPriceWithLocale(
                                      item.price,
                                      schema.price_mask,
                                      languageTag,
                                      true,
                                      false
                                    )}`
                                  }}
                                />
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {viewport.mobile && (
              <div className={style.saving}>
                <div className={style.title}>{t('questions', 'ec', 'target', 'potential')}</div>
                <div className={style.row}>
                  <div className={style.imagePromo} />
                  <div className={style.text}>{t('questions', 'ec', 'target', 'promo')}</div>
                  <EmptyLink color="#CC0000">
                    <div className={style.add}>{t('questions', 'ec', 'target', 'add')}</div>
                  </EmptyLink>
                </div>
              </div>
            )}
            {!viewport.mobile && (
              <>
                <div className={style.tabs}>
                  <div className={`${style.tab} ${style.tabActive}`}>
                    <EmptyLink color="#CC0000">{t('questions', 'ec', 'target', 'saveForLater')}</EmptyLink>
                  </div>
                  <div className={style.tab}>
                    <EmptyLink color="#CC0000">{t('questions', 'ec', 'target', 'fav')}</EmptyLink>
                  </div>
                </div>
                <div className={style.footer}>
                  <div className={style.image} />
                  <div className={style.text}>{t('questions', 'ec', 'target', 'saveItems')}</div>
                  <div
                    className={style.subtitle}
                    dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'target', 'subtitle') }}
                  />
                </div>
              </>
            )}
          </div>
          <div className={style.summary}>
            <div className={style.order}>
              <div className={style.title}>{t('questions', 'ec', 'target', 'summary')}</div>
              <div className={style.item}>
                <div className={style.imageTotal} />
                <div className={style.info}>
                  <span
                    className={style.textBold}
                    dangerouslySetInnerHTML={{
                      __html: `${formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)} ${t(
                        'questions',
                        'ec',
                        'target',
                        'total'
                      )}`
                    }}
                  />
                  <span
                    className={style.textLight}
                    dangerouslySetInnerHTML={{
                      __html: t<(total: number) => string>('questions', 'ec', 'target', 'items')(totals[0])
                    }}
                  />
                </div>
                <button className={style.openSummary} onClick={() => setOpenSummary(state => !state)}>
                  {openSummary ? <i className="pi pi-chevron-up" /> : <i className="pi pi-chevron-down" />}
                </button>
              </div>
              {openSummary && (
                <>
                  <div className={style.row}>
                    <span
                      className={style.rowText}
                      dangerouslySetInnerHTML={{
                        __html: t<(items: number) => string>('questions', 'ec', 'target', 'summaryHead')(totals[0])
                      }}
                    />
                    <span
                      className={style.rowText}
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(totals[1], schema.price_mask, languageTag, true, false)
                      }}
                    />
                  </div>
                  <div className={style.row}>
                    <span className={style.rowText}>{t('questions', 'ec', 'target', 'shipping')}</span>
                    <span className={style.rowText}>{t('questions', 'ec', 'target', 'free')}</span>
                  </div>
                  <div className={style.row}>
                    <span className={style.rowText}>{t('questions', 'ec', 'target', 'taxes')}</span>
                    <span
                      className={style.rowText}
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(totals[1] * 0.07, schema.price_mask, languageTag, true, false)
                      }}
                    />
                  </div>
                  <div className={style.row}>
                    <strong>{t('questions', 'ec', 'target', 'total')}</strong>
                    <strong
                      dangerouslySetInnerHTML={{
                        __html: formatPriceWithLocale(
                          totals[1] + (totals[1] / 100) * 7,
                          schema.price_mask,
                          languageTag,
                          true,
                          false
                        )
                      }}
                    />
                  </div>
                </>
              )}
              <div className={style.line} />
              <div className={style.item}>
                <div className={style.imageCheck} />
                <div className={style.info}>
                  <span className={style.textBold}>{t('questions', 'ec', 'target', 'freeShipping')}</span>
                  <span className={style.textLight}>{t('questions', 'ec', 'target', 'exclusionsApply')}</span>
                </div>
                <EmptyLink color="#CC0000">
                  <i className="pi pi-info-circle" />
                </EmptyLink>
              </div>
              <div className={style.line} />
              <div className={style.item}>
                <div className={style.imageAffirm} />
                <div className={style.info}>
                  <span className={style.textBold}>{t('questions', 'ec', 'target', 'payLow')}</span>
                  <span className={style.textLight}>{t('questions', 'ec', 'target', 'affirm')}</span>
                </div>
                <EmptyLink color="#CC0000">
                  <i className="pi pi-info-circle" />
                </EmptyLink>
              </div>
              <div className={style.line} />
              <div className={style.item}>
                <div className={style.imagePresent} />
                <div className={style.info}>
                  <span className={style.textBold}>{t('questions', 'ec', 'target', 'gift')}</span>
                  <span className={style.textLight}>{t('questions', 'ec', 'target', 'giftMessage')}</span>
                </div>
                <EmptyLink color="#CC0000">
                  <div className={style.switch} />
                </EmptyLink>
              </div>

              {!viewport.mobile && (
                <>
                  {renderError()}
                  <button className={`${style.checkout} ${!valid ? style.disabled : ''}`} onClick={checkout}>
                    {t('questions', 'ec', 'target', 'checkout')}
                  </button>
                </>
              )}
            </div>
            {!viewport.mobile && (
              <div className={style.saving}>
                <div className={style.title}>{t('questions', 'ec', 'target', 'potential')}</div>
                <div className={style.row}>
                  <div className={style.imagePromo} />
                  <div className={style.text}>{t('questions', 'ec', 'target', 'promo')}</div>
                  <EmptyLink color="#CC0000">
                    <div className={style.add}>{t('questions', 'ec', 'target', 'add')}</div>
                  </EmptyLink>
                </div>
              </div>
            )}
          </div>
          {viewport.mobile && (
            <div className={style.checkoutWrapper}>
              {renderError()}
              <button className={`${style.checkoutMobile} ${!valid ? style.disabled : ''}`} onClick={checkout}>
                {t('questions', 'ec', 'target', 'checkout')}
              </button>
            </div>
          )}
        </div>
      ) : (
        <>
          <div className={`${style.cartEmpty} ${viewport.mobile ? style.cartEmptyMobile : ''}`}>
            <EmptyLink color="#CC0000">
              <div className={style.help}>{t('questions', 'ec', 'target', 'help')}</div>
            </EmptyLink>
            <div className={style.head}>{t('questions', 'ec', 'target', 'cartEmpty')}</div>
            <div
              className={style.subtext}
              dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'target', 'cartEmptyText') }}
            />
            <button className={style.goHome} onClick={onCartClose}>
              {t('questions', 'ec', 'target', 'home')}
            </button>
            {na && (
              <button className={style.naButton} onClick={onNaSelect}>
                {na.text || t('questions', 'ec', 'na')}
              </button>
            )}
            <div className={style.image}></div>
          </div>
          {!viewport.mobile && (
            <>
              <div className={style.tabs}>
                <div className={`${style.tab} ${style.tabActive}`}>
                  <EmptyLink color="#CC0000">{t('questions', 'ec', 'target', 'saved')}</EmptyLink>
                </div>
                <div className={style.tab}>
                  <EmptyLink color="#CC0000">{t('questions', 'ec', 'target', 'fav')}</EmptyLink>
                </div>
              </div>
              <div className={style.footer}>
                <div className={style.image} />
                <div className={style.text}>{t('questions', 'ec', 'target', 'saveItems')}</div>
                <div
                  className={style.subtitle}
                  dangerouslySetInnerHTML={{ __html: t('questions', 'ec', 'target', 'subtitle') }}
                />
              </div>
            </>
          )}
        </>
      )}
    </>
  )
}

export default Cart
